import { isNotNull, useAdminOrganizationsQuery, useOrgRolesQuery } from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  PageBody,
  Paper,
  Typography,
} from '@alice-financial/pretext-ui'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import * as React from 'react'
import { Authenticate } from '../auth/AuthenticateRoutes'
import { CreateOrgButton } from '../organization/onboarding/IntakeFormDialog'
import { orgRootPath } from '../orgDashboard/organization/useOrg'
import { RouteChrome } from '../routes/RouteChrome'
import { BrokerDashboardStats } from './BrokerDashboardStats'
import { OnboardingOrgCard } from './OnboardingOrgCard'
import { OrgListDataGrid } from './OrgListDataGrid'
import { OnboardingOrgRow } from './types'

const unsignedOrgFirst = (a: OnboardingOrgRow, b: OnboardingOrgRow) => {
  if (!a.org.contractSignedOn && !b.org.contractSignedOn) return 0
  if (!a.org.contractSignedOn) return -1
  if (!b.org.contractSignedOn) return 1
  return 0
}

export const BrokerDashboardHome = () => {
  const { data: orgRolesData } = useOrgRolesQuery()
  const { data: adminOrgsData, isLoading } = useAdminOrganizationsQuery()

  const orgAdminRoles = orgRolesData?.currentUser?.adminRoles || []
  const adminOrgs = adminOrgsData?.adminOrganizations.nodes || []

  // Create rows with pre-loaded organization data and individual loading states
  const rows: OnboardingOrgRow[] = adminOrgs.filter(isNotNull).map((org) => ({ id: org.id, org }))
  const readyToOnboardOrgRows = rows.filter((row) => !row.org.isOnboarded && !row.org.contractSignedOn)

  const onboardingOrgRows = rows
    .filter((row) => !row.org.isOnboarded && row.org.contractSignedOn)
    .sort(unsignedOrgFirst)
  const alreadyLaunchedOrgRows = rows.filter((row) => row.org.isOnboarded)

  return (
    <Authenticate requiredProfile={['multi_admin', 'payroll_partner']} accessCheck="orgAdminDashboard">
      <RouteChrome type="admin" root={{ route: '/manage', label: 'Admin' }}>
        <PageBody>
          <Container maxWidth="xl">
            <Box maxWidth="md">
              <Typography variant="h1" gutterBottom>
                Clients
              </Typography>
              <Typography variant="body2" gutterBottom>
                You have administrator access to multiple organizations. Track their onboarding progress and
                keep track of important updates here.
              </Typography>
              {orgAdminRoles.length > 0 && ( // only show create org button if user is an org admin
                <Box maxWidth="sm">
                  <CreateOrgButton dialog />
                </Box>
              )}
            </Box>

            {!isLoading && adminOrgsData?.adminOrganizations && (
              <BrokerDashboardStats adminOrganization={adminOrgsData.adminOrganizations} />
            )}

            {isLoading && (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            )}

            {!isLoading && readyToOnboardOrgRows.length > 0 && (
              <Box maxWidth="md" my={4}>
                <Typography variant="h2" gutterBottom>
                  Ready to onboard
                </Typography>
                <Box maxWidth="sm">
                  <Paper>
                    <List dense disablePadding>
                      {readyToOnboardOrgRows.map(
                        (row) =>
                          row.org && (
                            <ListItem key={row.org.id} divider>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight="bold">
                                    {row.org.name}
                                  </Typography>
                                }
                                secondary="Order form not signed"
                              />
                              <Button
                                variant="contained"
                                size="small"
                                component={Link}
                                to={orgRootPath(row.org.id)}
                                endIcon={<ArrowForwardIosIcon />}
                              >
                                Go to order form
                              </Button>
                            </ListItem>
                          )
                      )}
                    </List>
                  </Paper>
                </Box>
              </Box>
            )}
            {!isLoading && onboardingOrgRows.length > 0 && (
              <Box maxWidth="md" my={4}>
                <Typography variant="h2" gutterBottom>
                  Currently onboarding
                </Typography>
                {onboardingOrgRows.map((row) => (
                  <OnboardingOrgCard key={row.org.id} {...row} />
                ))}
              </Box>
            )}
            {!isLoading && alreadyLaunchedOrgRows.length > 0 && (
              <Box maxWidth="md" my={4}>
                <Typography variant="h2" gutterBottom>
                  Select the organization you would like to manage
                </Typography>
                <Paper elevation={1}>
                  <OrgListDataGrid rows={alreadyLaunchedOrgRows} />
                </Paper>
              </Box>
            )}
          </Container>
        </PageBody>
      </RouteChrome>
    </Authenticate>
  )
}
